import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { 
  Store, Package, ShoppingCart, MessageCircle, TrendingUp, Users, 
  Check, Menu, X, Sparkles, Zap, Shield, ArrowRight, Star,
  MapPin, Palette, Grid3x3
} from 'lucide-react';
import { getAllPlans, getApprovedReviews, getAllStores } from '../lib/firebaseService';
import { useAuth } from '../context/AuthContext';
import ReviewForm from '../components/ReviewForm';
import FloatingAd from '../components/FloatingAd';

export default function Landing() {
  const { firebaseUser } = useAuth();
  const [plans, setPlans] = useState([]);
  const [plansLoading, setPlansLoading] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [approvedReviews, setApprovedReviews] = useState([]);
  const [showcaseStores, setShowcaseStores] = useState([]);

  useEffect(() => {
    loadPlans();
    loadReviews();
    loadShowcaseStores();
  }, []);

  const loadPlans = async () => {
    try {
      const plansData = await getAllPlans();
      setPlans(plansData);
    } catch (error) {
      console.error('Planlar yüklənə bilmədi:', error);
    } finally {
      setPlansLoading(false);
    }
  };

  const loadReviews = async () => {
    try {
      const reviewsData = await getApprovedReviews();
      console.log('Loaded approved reviews:', reviewsData);
      setApprovedReviews(reviewsData);
    } catch (error) {
      console.error('Rəylər yüklənə bilmədi:', error);
    }
  };

  const loadShowcaseStores = async () => {
    try {
      const storesData = await getAllStores();
      // Yalnız showcase olaraq seçilmiş və aktiv mağazaları göstər
      const showcaseStores = storesData
        .filter(store => store.is_showcase && store.is_active !== false && store.logo_url)
        .slice(0, 6);
      setShowcaseStores(showcaseStores);
    } catch (error) {
      console.error('Mağazalar yüklənə bilmədi:', error);
    }
  };

  const features = [
    { 
      icon: Package, 
      title: 'Məhsul İdarəetməsi', 
      description: 'Məhsullarınızı asanlıqla əlavə edin və idarə edin',
      gradient: 'from-gray-700 to-gray-500'
    },
    { 
      icon: MessageCircle, 
      title: 'WhatsApp İnteqrasiyası', 
      description: 'Müştərilərlə sürətli əlaqə qurub sifarişləri WhatsApp ilə qəbul edin',
      gradient: 'from-gray-900 to-gray-700'
    },
    { 
      icon: Palette, 
      title: 'Qara & Ağ Tema', 
      description: 'Minimalist və professional görünüş',
      gradient: 'from-gray-800 to-gray-600'
    },
    { 
      icon: MapPin, 
      title: 'Konum Xəritəsi', 
      description: 'Müştərilər sizin ünvanınızı asanlıqla tapa bilər',
      gradient: 'from-gray-700 to-gray-500'
    },
  ];

  const benefits = [
    'Texniki biliyə ehtiyac yoxdur',
    'WhatsApp ilə birbaşa sifarişlər',
    'Pulsuz başlayın',
    'Asan istifadə',
    'Mobil uyğun',
    'Sürətli quraşdırma',
    'Subdomain dəstəği'
  ];

  const stats = [
    { number: '📱', label: 'Mobil Uyğun' },
    { number: '⚡', label: 'Kod Yazmadan Vebsayta Sahib Ol' },
    { number: '🚀', label: 'Sürətli Quraşdırma' },
    { number: '🕐', label: '7/24 Dəstək' },
  ];

  const faqs = [
    {
      question: 'Texniki biliyə ehtiyac varmı?',
      answer: 'Xeyr! Sistemimiz çox sadədir. Heç bir kodlaşdırma və ya texniki biliyə ehtiyac yoxdur. Sadəcə qeydiyyatdan keçin və mağazanızı qurun.'
    },
    {
      question: 'Ödəniş necə edilir?',
      answer: 'Sifarişlər birbaşa sizin WhatsApp nömrənizə gəlir. Müştərilərlə birbaşa əlaqə saxlayıb ödəniş metodunu siz seçirsiniz - nağd, köçürmə və ya kart.'
    },
    {
      question: 'Pulsuz planda hansı məhdudiyyətlər var?',
      answer: 'Pulsuz planda 3 məhsul limiti var. Premium plana keçməklə limitsiz məhsul əlavə edə bilərsiniz.'
    },
    {
      question: 'Platformanızın üstünlükləri nədir?',
      answer: 'Sürətli quraşdırma, WhatsApp inteqrasiyası, mobil uyğun dizayn, öz subdomain və logosu, real-time sifarişlər, heç bir kodlaşdırma tələb olunmur.'
    },
    {
      question: 'Mağazamı necə tanıda və satışları artıra bilərəm?',
      answer: 'Mağaza linkinizi sosial mediada (Instagram, Facebook, TikTok) paylaşın, WhatsApp Status-da yayın, Google My Business-ə əlavə edin. Reklam kampaniyaları həyata keçirin və məhsul təsvirlərini SEO-ya uyğun yazın.'
    },
    {
      question: 'Dəstək xidməti nə qədər sürətlidir?',
      answer: 'Dəstək komandamız 24 saat ərzində cavab verir. Texniki problemlərdə daha sürətli, əksər suallar 2-3 saat ərzində həll olunur.'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Floating Ad */}
      <FloatingAd />
      
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-lg border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-14 md:h-16">
            <Link to="/" className="flex items-center gap-2 group">
              <span className="text-xl md:text-2xl font-bold text-black">I Veb</span>
            </Link>

            {/* Desktop Menu */}
            <div className="hidden md:flex items-center gap-8">
              <a href="#features" className="text-gray-600 hover:text-black transition-colors font-medium">
                Xüsusiyyətlər
              </a>
              <a href="#pricing" className="text-gray-600 hover:text-black transition-colors font-medium">
                Qiymətlər
              </a>
              <Link to="/stores" className="text-gray-600 hover:text-black transition-colors font-medium">
                Mağazalar
              </Link>
              {firebaseUser ? (
                <Link to="/dashboard">
                  <Button className="bg-black hover:bg-gray-800 text-white">
                    Mağazaya keç
                  </Button>
                </Link>
              ) : (
                <>
                  <Link to="/login">
                    <Button variant="outline" className="border-2 border-black hover:bg-black hover:text-white transition-all">
                      Daxil ol
                    </Button>
                  </Link>
                  <Link to="/register">
                    <Button className="bg-black hover:bg-gray-800 text-white">
                      Başla
                    </Button>
                  </Link>
                </>
              )}
            </div>

            {/* Mobile Menu Button */}
            <button
              className="md:hidden p-2 hover:bg-gray-100 rounded-lg transition-colors"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-white border-t border-gray-100 shadow-xl animate-slide-down">
            <div className="px-4 py-4 space-y-2">
              <a href="#features" onClick={() => setMobileMenuOpen(false)} className="block px-4 py-3 rounded-xl text-gray-700 hover:bg-gray-100 transition-colors font-medium">
                Xüsusiyyətlər
              </a>
              <a href="#pricing" onClick={() => setMobileMenuOpen(false)} className="block px-4 py-3 rounded-xl text-gray-700 hover:bg-gray-100 transition-colors font-medium">
                Qiymətlər
              </a>
              <Link to="/stores" onClick={() => setMobileMenuOpen(false)} className="block px-4 py-3 rounded-xl text-gray-700 hover:bg-gray-100 transition-colors font-medium">
                Mağazalar
              </Link>
              <div className="pt-2 space-y-2">
                {firebaseUser ? (
                  <Link to="/dashboard" className="block">
                    <Button className="w-full bg-black hover:bg-gray-800 text-white py-3">
                      Mağazaya keç
                    </Button>
                  </Link>
                ) : (
                  <>
                    <Link to="/login" className="block">
                      <Button variant="outline" className="w-full border-2 border-black py-3">
                        Daxil ol
                      </Button>
                    </Link>
                    <Link to="/register" className="block">
                      <Button className="w-full bg-black hover:bg-gray-800 text-white py-3">
                        Pulsuz başla
                      </Button>
                    </Link>
                  </>
                )}
              </div>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section className="pt-20 md:pt-32 pb-12 md:pb-20 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 -z-10">
          <div className="absolute inset-0 bg-[linear-gradient(to_right,#80808008_1px,transparent_1px),linear-gradient(to_bottom,#80808008_1px,transparent_1px)] bg-[size:24px_24px]"></div>
        </div>

        <div className="max-w-7xl mx-auto">
          <div className="text-center space-y-6 md:space-y-8 animate-fade-in">
            {/* Badge */}
            <div className="inline-flex items-center gap-2 bg-black text-white px-3 py-1.5 md:px-4 md:py-2 rounded-full text-xs md:text-sm font-medium">
              <Sparkles className="w-3.5 h-3.5 md:w-4 md:h-4" />
              Online Mağaza Həlli
            </div>

            {/* Heading */}
            <h1 className="text-3xl sm:text-5xl lg:text-7xl font-bold text-black leading-tight">
              Online mağazanızı
              <br />
              <span className="bg-gradient-to-r from-gray-900 via-gray-600 to-gray-900 bg-clip-text text-transparent">
                asanlıqla yaradın
              </span>
            </h1>

            {/* Description */}
            <p className="text-base md:text-xl text-gray-600 max-w-2xl mx-auto leading-relaxed px-4">
              Kodlaşdırma bilmədən öz e-ticarət saytınızı yaradın və satışa başlayın.
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3 pt-2 md:pt-4 px-4">
              {firebaseUser ? (
                <Link to="/dashboard" className="w-full sm:w-auto">
                  <Button size="lg" className="w-full sm:w-auto bg-black hover:bg-gray-800 text-white px-8 py-5 md:py-6 text-base md:text-lg group rounded-xl">
                    Mağazaya keç
                    <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </Link>
              ) : (
                <Link to="/register" className="w-full sm:w-auto">
                  <Button size="lg" className="w-full sm:w-auto bg-black hover:bg-gray-800 text-white px-8 py-5 md:py-6 text-base md:text-lg group rounded-xl">
                    Pulsuz başla
                    <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </Link>
              )}
              <Link to="/stores" className="w-full sm:w-auto">
                <Button size="lg" variant="outline" className="w-full sm:w-auto border-2 border-black px-8 py-5 md:py-6 text-base md:text-lg hover:bg-black hover:text-white transition-all rounded-xl">
                  Mağazalara bax
                </Button>
              </Link>
            </div>

            {/* Stats - Mobile Friendly */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 md:gap-8 pt-8 md:pt-16 px-2">
              {stats.map((stat, index) => (
                <div key={index} className="text-center p-4 md:p-0 bg-gray-50 md:bg-transparent rounded-2xl">
                  <div className="text-2xl md:text-4xl font-bold text-black mb-1">{stat.number}</div>
                  <div className="text-gray-500 text-xs md:text-sm">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-12 md:py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-10 md:mb-16 animate-fade-in">
            <div className="inline-block bg-black text-white px-3 py-1 rounded-full text-xs md:text-sm font-medium mb-3 md:mb-4">
              Xüsusiyyətlər
            </div>
            <h2 className="text-2xl sm:text-4xl md:text-5xl font-bold text-black mb-3 md:mb-4">
              Hər şey bir yerdə
            </h2>
            <p className="text-base md:text-xl text-gray-600 max-w-2xl mx-auto">
              Mağazanızı idarə etmək üçün lazım olan bütün alətlər
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-8">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <div
                  key={index}
                  className="group bg-white rounded-2xl p-5 md:p-8 border border-gray-200 hover:border-black transition-all duration-300 hover:shadow-xl active:scale-[0.98]"
                >
                  <div className={`w-12 h-12 md:w-14 md:h-14 bg-gradient-to-br ${feature.gradient} rounded-xl flex items-center justify-center mb-4 md:mb-6 group-hover:scale-110 transition-transform`}>
                    <Icon className="w-6 h-6 md:w-7 md:h-7 text-white" />
                  </div>
                  <h3 className="text-lg md:text-xl font-bold text-black mb-2 md:mb-3">{feature.title}</h3>
                  <p className="text-sm md:text-base text-gray-600 leading-relaxed">{feature.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-black text-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <div className="animate-fade-in">
              <div className="inline-block bg-white text-black px-4 py-1 rounded-full text-sm font-medium mb-4">
                Niyə Biz?
              </div>
              <h2 className="text-4xl sm:text-5xl font-bold mb-6">
                Ən sadə və sürətli həll
              </h2>
              <p className="text-gray-400 text-lg mb-8 leading-relaxed">
                Texniki biliyə ehtiyac olmadan öz online mağazanızı yaradın və satışa başlayın.
              </p>
              <Link to="/register">
                <Button size="lg" className="bg-white text-black hover:bg-gray-100 px-8 py-6 text-lg group">
                  İndi Başla
                  <Zap className="w-5 h-5 ml-2 group-hover:rotate-12 transition-transform" />
                </Button>
              </Link>
            </div>

            <div className="grid grid-cols-2 gap-4 animate-fade-in" style={{ animationDelay: '200ms' }}>
              {benefits.map((benefit, index) => (
                <div
                  key={index}
                  className="flex items-center gap-3 bg-white/5 backdrop-blur rounded-xl p-4 border border-white/10 hover:bg-white/10 transition-all"
                >
                  <Check className="w-5 h-5 text-green-400 flex-shrink-0" />
                  <span className="text-sm font-medium">{benefit}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Real Mağazalar Section - Xüsusiyyətlər altında */}
      {showcaseStores.length > 0 && (
        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl sm:text-5xl font-bold text-black mb-8">
                Real Mağazalar
              </h2>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              {showcaseStores.slice(0, 3).map((store) => (
                <a
                  key={store.id}
                  href={`/store/${store.id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 border border-gray-100"
                >
                  <div className="relative h-48 bg-gray-50 flex items-center justify-center p-6">
                    {store.logo_url ? (
                      <img
                        src={store.logo_url}
                        alt={store.store_name}
                        className="max-w-full max-h-full object-contain group-hover:scale-110 transition-transform duration-500"
                      />
                    ) : (
                      <Store className="w-16 h-16 text-gray-300" />
                    )}
                    <div className="absolute top-4 right-4 bg-black text-white px-3 py-1 rounded-full text-xs font-medium">
                      Aktiv
                    </div>
                  </div>
                  <div className="p-6">
                    <h4 className="font-bold text-xl text-black mb-2">{store.store_name}</h4>
                    <p className="text-sm text-gray-500 mb-4">
                      {store.subdomain ? `${store.subdomain}.iveb.az` : 'Mağaza'}
                    </p>
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <Check className="w-4 h-4 text-green-500" />
                      Aktiv mağaza
                    </div>
                  </div>
                </a>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Pricing Section */}
      <section id="pricing" className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16 animate-fade-in">
            <div className="inline-block bg-black text-white px-4 py-1 rounded-full text-sm font-medium mb-4">
              Qiymətlər
            </div>
            <h2 className="text-4xl sm:text-5xl font-bold text-black mb-4">
              Sizə uyğun planı seçin
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Pulsuz başlayın, böyüdükcə yüksəldin
            </p>
          </div>

          {plansLoading ? (
            <div className="text-center py-12">
              <div className="animate-spin w-12 h-12 border-4 border-black border-t-transparent rounded-full mx-auto"></div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {plans.map((plan, index) => (
                <div
                  key={plan.id}
                  className={`bg-white rounded-2xl p-8 border-2 transition-all duration-300 hover:shadow-2xl hover:-translate-y-2 animate-fade-in ${
                    plan.name === 'PROFESSIONAL' ? 'border-black scale-105' : 'border-gray-200 hover:border-black'
                  }`}
                  style={{ animationDelay: `${index * 100}ms` }}
                >
                  {plan.name === 'PROFESSIONAL' && (
                    <div className="bg-black text-white text-xs font-bold px-3 py-1 rounded-full inline-block mb-4">
                      ƏN POPULYAR
                    </div>
                  )}
                  <h3 className="text-2xl font-bold text-black mb-2">{plan.name}</h3>
                  <div className="mb-6">
                    <span className="text-4xl font-bold text-black">{plan.price_monthly || 0}</span>
                    <span className="text-gray-600"> AZN/ay</span>
                  </div>
                  <ul className="space-y-3 mb-8">
                    {Array.isArray(plan.features) && plan.features.map((feature, idx) => (
                      <li key={idx} className="flex items-center gap-2 text-gray-600">
                        <Check className="w-5 h-5 text-green-500 flex-shrink-0" />
                        <span className="text-sm">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  <Button
                    className={`w-full ${
                      plan.name === 'PROFESSIONAL'
                        ? 'bg-black hover:bg-gray-800 text-white'
                        : 'bg-gray-100 hover:bg-black hover:text-white text-black'
                    } transition-all`}
                    onClick={() => {
                      if (plan.purchase_link) {
                        window.open(plan.purchase_link, '_blank');
                      } else {
                        window.location.href = '/register';
                      }
                    }}
                  >
                    {plan.name === 'FREE' ? 'Başla' : 'Seç'}
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <div className="inline-block bg-black text-white px-4 py-1 rounded-full text-sm font-medium mb-4">
              <Star className="w-4 h-4 inline mr-1" />
              Müştəri Rəyləri
            </div>
            <h2 className="text-4xl sm:text-5xl font-bold text-black mb-4">
              Uğur Hekayələri
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Real istifadəçilərimizin təcrübələri
            </p>
          </div>

          {approvedReviews.length > 0 ? (
            <div className="grid md:grid-cols-3 gap-8">
              {approvedReviews.slice(0, 3).map((review) => (
                <div
                  key={review.id}
                  className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 border border-gray-100"
                >
                  <div className="flex items-center gap-4 mb-6">
                    <div className="w-16 h-16 rounded-full bg-black text-white flex items-center justify-center text-xl font-bold">
                      {review.name.charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <h4 className="font-bold text-black">{review.name}</h4>
                      <p className="text-sm text-gray-600">{review.business || 'Müştəri'}</p>
                    </div>
                  </div>
                  
                  <div className="flex gap-1 mb-4">
                    {[...Array(review.rating)].map((_, i) => (
                      <Star key={i} className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                    ))}
                  </div>

                  <p className="text-gray-700 leading-relaxed">"{review.text}"</p>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <div className="bg-white rounded-2xl p-12 shadow-lg max-w-2xl mx-auto">
                <MessageCircle className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                <h3 className="text-xl font-semibold text-gray-700 mb-2">Hələ ki rəy yoxdur</h3>
                <p className="text-gray-500 mb-6">
                  İlk rəyi siz yazın və platformamız haqqında düşüncələrinizi paylaşın!
                </p>
                <Button
                  onClick={() => {
                    const reviewForm = document.getElementById('review-form');
                    if (reviewForm) {
                      reviewForm.scrollIntoView({ behavior: 'smooth' });
                    }
                  }}
                  className="bg-black hover:bg-gray-800"
                >
                  Rəy Yaz
                </Button>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Showcase Section */}
      {/* FAQ Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-16">
            <div className="inline-block bg-black text-white px-4 py-1 rounded-full text-sm font-medium mb-4">
              <MessageCircle className="w-4 h-4 inline mr-1" />
              Suallar
            </div>
            <h2 className="text-4xl sm:text-5xl font-bold text-black mb-4">
              Tez-tez Verilən Suallar
            </h2>
            <p className="text-xl text-gray-600">
              Suallarınıza cavab tapın
            </p>
          </div>

          <div className="space-y-4">
            {faqs.map((faq, idx) => (
              <details
                key={idx}
                className="group bg-white rounded-xl p-6 shadow-sm hover:shadow-md transition-all cursor-pointer border border-gray-100"
              >
                <summary className="flex justify-between items-center font-semibold text-lg text-black list-none">
                  <span>{faq.question}</span>
                  <span className="transition group-open:rotate-180">
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </span>
                </summary>
                <p className="mt-4 text-gray-600 leading-relaxed">
                  {faq.answer}
                </p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Review Form */}
      <ReviewForm />

      {/* Trust Badges */}
      <section className="py-12 px-4 sm:px-6 lg:px-8 border-t border-gray-200">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-wrap justify-center items-center gap-8 md:gap-16">
            <div className="flex items-center gap-2 text-gray-600">
              <Shield className="w-6 h-6 text-green-600" />
              <span className="font-medium">SSL Şifrələmə</span>
            </div>
            <div className="flex items-center gap-2 text-gray-600">
              <Check className="w-6 h-6 text-green-600" />
              <span className="font-medium">99.9% Uptime</span>
            </div>
            <div className="flex items-center gap-2 text-gray-600">
              <Zap className="w-6 h-6 text-yellow-600" />
              <span className="font-medium">Sürətli Yükləmə</span>
            </div>
            <div className="flex items-center gap-2 text-gray-600">
              <Users className="w-6 h-6 text-blue-600" />
              <span className="font-medium">24/7 Dəstək</span>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-black text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff12_1px,transparent_1px),linear-gradient(to_bottom,#ffffff12_1px,transparent_1px)] bg-[size:24px_24px]"></div>
        
        <div className="max-w-4xl mx-auto text-center relative z-10 animate-fade-in">
          <div className="inline-block bg-white text-black px-4 py-1 rounded-full text-sm font-medium mb-6">
            <Star className="w-4 h-4 inline mr-1" />
            Hazırsınız?
          </div>
          <h2 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6">
            Online satışa bu gün başlayın
          </h2>
          <p className="text-xl text-gray-400 mb-10 max-w-2xl mx-auto leading-relaxed">
            Kredit kartı tələb olunmur. İndi qeydiyyatdan keçin və satışa başlayın.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link to="/register">
              <Button size="lg" className="bg-white text-black hover:bg-gray-100 px-10 py-7 text-lg font-semibold group">
                Pulsuz Başla
                <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-2 transition-transform" />
              </Button>
            </Link>
            <Link to="/stores">
              <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-black px-10 py-7 text-lg font-semibold transition-all">
                Demo Bax
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-white border-t border-gray-200 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-8">
            <div>
              <Link to="/" className="flex items-center gap-2 mb-4">
                <span className="text-xl font-bold text-black">I Veb</span>
              </Link>
              <p className="text-gray-600 text-sm">
                Online mağazanızı asanlıqla yaradın
              </p>
            </div>
            
            <div>
              <h4 className="font-bold text-black mb-4">Məhsul</h4>
              <ul className="space-y-2 text-sm">
                <li><a href="#features" className="text-gray-600 hover:text-black transition-colors">Xüsusiyyətlər</a></li>
                <li><a href="#pricing" className="text-gray-600 hover:text-black transition-colors">Qiymətlər</a></li>
                <li><Link to="/stores" className="text-gray-600 hover:text-black transition-colors">Mağazalar</Link></li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-bold text-black mb-4">Şirkət</h4>
              <ul className="space-y-2 text-sm">
                <li><a href="#" className="text-gray-600 hover:text-black transition-colors">Haqqımızda</a></li>
                <li><a href="#" className="text-gray-600 hover:text-black transition-colors">Bloq</a></li>
                <li><a href="#" className="text-gray-600 hover:text-black transition-colors">Karyera</a></li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-bold text-black mb-4">Dəstək</h4>
              <ul className="space-y-2 text-sm">
                <li><a href="#" className="text-gray-600 hover:text-black transition-colors">Kömək Mərkəzi</a></li>
                <li><a href="#" className="text-gray-600 hover:text-black transition-colors">Əlaqə</a></li>
                <li><a href="#" className="text-gray-600 hover:text-black transition-colors">Status</a></li>
              </ul>
            </div>
          </div>
          
          <div className="border-t border-gray-200 pt-8 text-center text-sm text-gray-600">
            <p>© {new Date().getFullYear()} I Veb. Bütün hüquqlar qorunur.</p>
          </div>
        </div>
      </footer>

    </div>
  );
}
